// Apolo IDE — PlanSkillsPalette (sprint plan-tab-v1, E6).
//
// Command palette centralizada (⌘⇧K) com busca fuzzy + categorias por
// fase + preview à direita. Escolha vira um block 'skill' no canvas.

import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import * as Icons from 'lucide-react';
import { useInlineSkills } from '../../hooks/useInlineSkills.js';

const PHASE_LABEL = {
  discovery: '🔍 Discovery',
  architecture: '🏗 Architecture',
  database: '🗃 Database',
  delivery: '🚀 Delivery',
  ui: '🎨 UI',
  ux: '👁 UX',
  security: '🔒 Security',
  qa: '🧪 QA',
};

function score(skill, query) {
  if (!query) return 1;
  const q = query.toLowerCase();
  const hay = `${skill.name} ${skill.label} ${skill.phase} ${skill.short}`.toLowerCase();
  if (hay.includes(q)) {
    // Boost: matches in label, then name, then short.
    let s = 1;
    if ((skill.label || '').toLowerCase().includes(q)) s += 3;
    if ((skill.name || '').toLowerCase().includes(q)) s += 2;
    if ((skill.phase || '').toLowerCase().includes(q)) s += 1;
    return s;
  }
  return 0;
}

export default function PlanSkillsPalette({ open, onClose, onPick }) {
  const { skills, loading, error } = useInlineSkills();
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const ranked = useMemo(() => {
    if (!skills?.length) return [];
    return skills
      .map((s) => ({ s, sc: score(s, query) }))
      .filter((r) => r.sc > 0)
      .sort((a, b) => b.sc - a.sc)
      .map((r) => r.s);
  }, [skills, query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  useEffect(() => { setActiveIdx(0); }, [query]);

  useEffect(() => {
    function esc(e) { if (e.key === 'Escape' && open) onClose(); }
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [open, onClose]);

  function handleKeyNav(e) {
    if (!ranked.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(ranked.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); const sk = ranked[activeIdx]; if (sk) { onPick(sk); onClose(); } }
  }

  const active = ranked[activeIdx];
  const ActiveIcon = active ? (Icons[active.icon] || Icons.Sparkles) : Icons.Sparkles;

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label="Catálogo de skills"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16 }}
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 p-4 pt-[15vh] backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: -10, scale: 0.97 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: -10, scale: 0.97 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="flex w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-apolo-plan-accentBorder bg-apolo-surface1"
            style={{ boxShadow: 'var(--apolo-plan-glow-wide), 0 20px 60px rgba(0,0,0,0.4)' }}
          >
            {/* Search bar */}
            <div className="flex items-center gap-2 border-b border-apolo-borderSubtle px-4 py-3">
              <Icons.Sparkles size={16} className="text-apolo-plan-accent" aria-hidden="true" />
              <input
                ref={inputRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyNav}
                placeholder="Busque skill por nome, fase ou palavra-chave…"
                className="h-9 flex-1 border-0 bg-transparent text-sm text-apolo-textPrimary placeholder:text-apolo-textPlaceholder focus:outline-none"
                aria-label="Buscar skill"
                aria-controls="plan-skills-listbox"
                role="combobox"
                aria-expanded
              />
              <span className="rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-0.5 text-[10px] text-apolo-textTertiary">⌘⇧K</span>
            </div>

            <div className="flex h-[480px]">
              {/* List */}
              <ul
                ref={listRef}
                id="plan-skills-listbox"
                role="listbox"
                className="flex w-[58%] flex-col overflow-y-auto border-r border-apolo-borderSubtle px-1 py-1"
              >
                {loading ? (
                  <li className="px-3 py-4 text-xs text-apolo-textTertiary">Carregando…</li>
                ) : error ? (
                  <li className="px-3 py-4 text-xs text-apolo-error">{error}</li>
                ) : ranked.length === 0 ? (
                  <li className="px-3 py-4 text-xs text-apolo-textTertiary">Sem skills.</li>
                ) : (
                  ranked.map((sk, idx) => {
                    const Icon = Icons[sk.icon] || Icons.Sparkles;
                    const isActive = idx === activeIdx;
                    return (
                      <li key={sk.name} role="option" aria-selected={isActive}>
                        <button
                          type="button"
                          onMouseEnter={() => setActiveIdx(idx)}
                          onClick={() => { onPick(sk); onClose(); }}
                          className={`flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left transition focus:outline-none ${
                            isActive
                              ? 'bg-apolo-plan-accentMuted'
                              : 'hover:bg-apolo-surface2'
                          }`}
                        >
                          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md" style={{ background: `${sk.color}22`, color: sk.color }} aria-hidden="true">
                            <Icon size={14} />
                          </span>
                          <span className="flex flex-1 flex-col leading-tight">
                            <span className="text-xs font-semibold text-apolo-textPrimary">{sk.label}</span>
                            <span className="truncate text-[10px] text-apolo-textTertiary">{sk.name} · {PHASE_LABEL[sk.phase] || sk.phase}</span>
                          </span>
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>

              {/* Preview */}
              <div className="flex w-[42%] flex-col p-4">
                {active ? (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: `${active.color}22`, color: active.color }} aria-hidden="true">
                        <ActiveIcon size={20} />
                      </span>
                      <div>
                        <h3 className="text-base font-semibold tracking-tight text-apolo-textPrimary">{active.label}</h3>
                        <p className="text-[10px] uppercase tracking-wide text-apolo-textTertiary">{PHASE_LABEL[active.phase] || active.phase}</p>
                      </div>
                    </div>
                    <p className="mt-4 text-sm leading-relaxed text-apolo-textSecondary">{active.short}</p>
                    <p className="mt-2 text-[11px] text-apolo-textTertiary">Slug: <code className="rounded bg-apolo-surface2 px-1 py-0.5 font-mono">{active.name}</code></p>
                    <div className="mt-auto pt-4">
                      <button
                        type="button"
                        onClick={() => { onPick(active); onClose(); }}
                        className="inline-flex w-full items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold text-white transition hover:brightness-110"
                        style={{ background: 'var(--apolo-plan-gradient)', boxShadow: 'var(--apolo-plan-glow-tight)' }}
                      >
                        <Icons.Plus size={14} /> Adicionar ao canvas
                      </button>
                      <p className="mt-2 text-center text-[10px] text-apolo-textTertiary">Enter / clique pra inserir</p>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-1 items-center justify-center text-xs text-apolo-textTertiary">
                    {ranked.length === 0 && !loading ? 'Nada por aqui.' : 'Selecione uma skill…'}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
